import { Link } from "react-router-dom";
import { MapPin, Mail, Phone } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const ensureHttps = (url: string | null | undefined) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `https://${url}`;
};

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const { data: settings } = useQuery({
    queryKey: ["siteSettings", "footer"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("*")
        .single();
      
      if (error) throw error;
      return data;
    },
  });

  return (
    <footer className="bg-muted/30 border-t mt-auto">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* About Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <img 
                src={settings?.footer_logo_url || "/src/assets/vitality-awakening-logo.png"} 
                alt="Vitality Awakening" 
                className="h-10 w-auto"
                width="40"
                height="40"
              />
            </div>
            {settings?.about_text && (
              <p className="text-sm text-muted-foreground leading-relaxed text-justify">
                {settings.about_text}
              </p>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/journeys" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Learning Journeys
                </Link>
              </li>
              <li>
                <Link to="/sessions" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Events & Webinars
                </Link>
              </li>
              <li>
                <Link to="/community" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Community
                </Link>
              </li>
              <li>
                <Link to="/members" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Member Directory
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="font-semibold mb-4">Resources</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Dashboard
                </Link>
              </li>
              <li>
                <Link to="/coaching" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Coaching Sessions
                </Link>
              </li>
              {settings?.privacy_policy && (
                <li>
                  <Link to="/privacy-policy" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    Privacy Policy
                  </Link>
                </li>
              )}
              {settings?.terms_of_service && (
                <li>
                  <Link to="/terms-of-service" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    Terms of Service
                  </Link>
                </li>
              )}
              {settings?.faq_content && (
                <li>
                  <Link to="/faq" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    FAQ
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              {settings?.location_address && (
                <li className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-primary mt-1 shrink-0" />
                  <span className="text-sm text-muted-foreground">
                    {settings.location_address}
                  </span>
                </li>
              )}
              {settings?.contact_email && (
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-primary shrink-0" />
                  <a 
                    href={`mailto:${settings.contact_email}`}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {settings.contact_email}
                  </a>
                </li>
              )}
              {settings?.contact_phone && (
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-primary shrink-0" />
                  <a 
                    href={`tel:${settings.contact_phone.replace(/\s/g, '')}`}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {settings.contact_phone}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground text-center md:text-left">
              © {currentYear} Vitality Awakening. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              {settings?.facebook_url && (
                <a 
                  href={ensureHttps(settings.facebook_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  aria-label="Facebook"
                >
                  Facebook
                </a>
              )}
              {settings?.instagram_url && (
                <a 
                  href={ensureHttps(settings.instagram_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  aria-label="Instagram"
                >
                  Instagram
                </a>
              )}
              {settings?.linkedin_url && (
                <a 
                  href={ensureHttps(settings.linkedin_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  aria-label="LinkedIn"
                >
                  LinkedIn
                </a>
              )}
              {settings?.youtube_url && (
                <a 
                  href={ensureHttps(settings.youtube_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  aria-label="YouTube"
                >
                  YouTube
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
